import useToogle from "hooks/useToggle";
import { AppDispatch, RootState } from "store";
import { fetchSessionPlan } from "store/reducers/livestreamSettings";
import { Text } from "@radix-ui/themes";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { RiArrowDropDownLine, RiArrowDropUpLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import VideoControl from "../controls/Video";
import AudioControl from "../controls/Audio";

interface SubSegmentProps {
  item: any;
}

const SubSegment = ({ item }: SubSegmentProps) => {
  return (
    <div className="bg-[#2d2b45] text-white p-3 rounded-lg border-l-4 border-app-blue mb-2">
      <p className="text-sm font-medium">{item.activity_name}</p>
    </div>
  );
};

interface SegmentProps {
  item: any;
  isFirst: boolean;
  isLast: boolean;
  refProp?: any
}

const Segment = ({ item, isFirst, isLast, refProp }: SegmentProps) => {
  const borderRadius = `
    ${isFirst ? 'rounded-t-xl' : ''}
    ${isLast ? 'rounded-b-xl' : ''}`;
  const isOngoing = item.status.toLowerCase() === "launch";
  const [isOn, setToogle] = useToogle();

  return (
    <div
      ref={refProp}
      className={`
        relative flex items-start gap-2 my-2 p-3 shadow-md transition-all duration-300
        ${borderRadius}
        ${isOngoing ? "bg-[#E9BE50] text-black" : "bg-[#3B4350] text-white"}
      `}
      style={isOngoing ? { boxShadow: "0 2px 16px 0 rgba(33, 32, 37, 0.10)" } : {}}
    >
      {isOngoing && (
        <div className="w-[5px] h-[35px] flex-shrink-0 bg-[#3B4350] rounded-md"></div>
      )}

      <div className="flex justify-between w-full">
        <div>
          <p className="text-xs font-semibold">{item?.activityName}:</p>
          <p className="text-sm font-semibold">
            {item?.activityData.title || item?.activityData?.role_play_title}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm">{item?.activityData?.duration || 0} Mins</p>
          <p
            className={`${item?.status.toLowerCase() === "ongoing"
              ? "text-xs text-[#E9BE50] capitalize"
              : "text-xs capitalize"
              }`}
          >
            {item?.status.toLowerCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

const SessionPlanPanel = () => {
  const { session } = useSelector((state: RootState) => state.live.settings);
  const sessionActivityData = useSelector((state: RootState) => state.live.settings.liveSessionActivityData);

  const [activityData, setActivityData] = useState<any>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const filtered = sessionActivityData.filter((item: any) => item.activityData.title !== 'Activity Transition Time');
    setActivityData(filtered);
  }, [sessionActivityData])

  useLayoutEffect(() => {
    if (!activityData.length) return;

    const ongoingIndex = activityData.findIndex(
      (item: any) => item.status.toLowerCase() === "launch"
    );
    if (ongoingIndex === -1) return;

    const segmentEl = segmentRefs.current[ongoingIndex];
    const containerEl = scrollContainerRef.current;

    if (segmentEl && containerEl) {
      // Scroll so the segment is centered in view
      const containerHeight = containerEl.offsetHeight;
      const segmentTop = segmentEl.offsetTop;
      const segmentHeight = segmentEl.offsetHeight;
      const scrollPos = segmentTop - containerHeight / 2 + segmentHeight / 2;

      containerEl.scrollTo({ top: scrollPos, behavior: "smooth" });
    }
  }, [activityData]);

  // Clear refs before mapping
  segmentRefs.current = [];

  return (
    <div className="bg-[#24272C] h-full w-full  rounded-tl-3xl rounded-bl-3xl shadow-lg text-white flex flex-col overflow-y-auto scrollbar-hide" >
      <div className=" flex justify-center p-4">
        <h2 className="text-lg w-full text-center font-bold text-white">
          Session Plan
        </h2>
      </div>
      <div className="flex flex-col h-full rounded-t-xl pt-2 border-t-[#3B4350] " style={{ boxShadow: 'inset -5px -5px 20px rgba(255, 255, 255, 0.25), inset 5px 5px 20px rgba(0, 0, 0, 0.25)' }}>
        {/* Session Header */}
        <div className="bg-[#5F488A] p-3 rounded-t-xl border-b-[#E9BE50]" >
          <Text className="text-sm font-semibold text-white">
            Session Name: {session?.session_name}
          </Text>
          <div className="flex justify-between text-white text-xs mt-2 text-gray-300">
            <Text>{session?.startDate}</Text>
            <Text>
              {session?.startTime} - {session?.endTime}
            </Text>
            <Text></Text>
          </div>
        </div>

        {/* Scrollable Segment List */}
        <div
          className="flex-1 overflow-y-auto scrollbar-hide px-1"
          ref={scrollContainerRef}
        >
          {activityData?.map((el: any, index: number) => (
            <Segment
              key={index}
              item={el}
              isFirst={index === 0}
              isLast={index === activityData.length - 1}
              refProp={(el) => (segmentRefs.current[index] = el)}
            />
          ))}
        </div>

        {/* Sticky Bottom Controls */}
        {/* <div className="sticky bottom-0 pt-2 pb-28 flex justify-center gap-4 shadow-md z-10">
          <VideoControl />
          <AudioControl />
        </div> */}
      </div>
    </div>
  );
};

export default SessionPlanPanel;
